import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useI18n } from '../../context/i18nContext';
import { ROLE_LABELS, ROLE_COLORS } from '../../constants/roles';
import ProfileDrawer from '../../components/ProfileDrawer';
import '../../styles/AdminHomePage.css';

// ---- CSS-only mini bar chart ----
const BarChart = ({ data, color, label }) => {
  if (!data || data.length === 0) return null;
  const max = Math.max(...data.map(d => d.count), 1);

  return (
    <div className="chart-wrap">
      <div className="chart-label">{label}</div>
      <div className="chart-bars">
        {data.map((d, i) => (
          <div key={i} className="chart-col" title={`${d.day}: ${d.count}`}>
            <div
              className="chart-bar"
              style={{ height: `${Math.max(4, (d.count / max) * 100)}%`, background: color }}
            />
            <div className="chart-day">{String(d.day).slice(8)}</div>
          </div>
        ))}
      </div>
    </div>
  );
};

const fillDays = (trend, days = 14) => {
  const result = [];
  for (let i = days - 1; i >= 0; i--) {
    const d = new Date();
    d.setDate(d.getDate() - i);
    const dayStr = d.toISOString().slice(0, 10);
    const found = trend.find(t => t.day === dayStr || String(t.day).startsWith(dayStr));
    result.push({ day: dayStr, count: found ? Number(found.count) : 0 });
  }
  return result;
};

const RoleBadge = ({ role }) => {
  return (
    <span className={`role-badge role-badge--${role}`}>
      {ROLE_LABELS[role] ?? `Role ${role}`}
    </span>
  );
};

const ACTION_ICONS = {
  login: '🟢', logout: '🔵', register: '✨',
  login_failed: '🔴', account_locked: '🔒',
  email_verified: '✅', password_reset: '🔑',
  user_deleted: '🗑️', invitation_sent: '📨',
  bulk_activate: '⚡', bulk_delete: '🗑️',
};

const AdminHomePage = () => {
  const { api, user } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  const [drawerUserId, setDrawerUserId] = useState(null);

  const [stats, setStats]  = useState(null);
  const [loading, setLoad] = useState(true);

  useEffect(() => {
    api.get('/admin/stats')
      .then(r => setStats(r.data))
      .catch(() => {
        api.get('/admin/users').then(r => {
          const users = r.data.users;
          setStats({
            totals: {
              total:      users.length,
              active:     users.filter(u => u.is_active).length,
              superadmins: users.filter(u => u.role === 0).length,
              admins:     users.filter(u => u.role === 1).length,
              users:      users.filter(u => u.role === 2).length,
              unverified: users.filter(u => !u.email_verified).length,
              deleted:    0,
            },
            regTrend: [], loginTrend: [], failedTrend: [],
            recentActivity: [], recentUsers: users.slice(0, 5),
          });
        });
      })
      .finally(() => setLoad(false));
  }, [api]);

  if (loading) return <div className="dash-loading">{t('common.loading')}</div>;
  if (!stats)  return null;

  const rawTotals = stats.totals || {};
  const totals = Object.fromEntries(
    Object.entries(rawTotals).map(([k, v]) => [k, Number(v) || 0])
  );
  const { regTrend, loginTrend, failedTrend, recentActivity, recentUsers,
          caseTotals, caseTrend, recentCases,
          propertyTotals, partnerTotals, invitationTotals } = stats;
  const reg    = fillDays(regTrend || []);
  const login  = fillDays(loginTrend || []);
  const failed = fillDays(failedTrend || []);
  const cases  = fillDays(caseTrend  || []);
  const ct  = Object.fromEntries(Object.entries(caseTotals   || {}).map(([k,v]) => [k, Number(v)||0]));
  const pt  = Object.fromEntries(Object.entries(propertyTotals || {}).map(([k,v]) => [k, Number(v)||0]));
  const prt = Object.fromEntries(Object.entries(partnerTotals  || {}).map(([k,v]) => [k, Number(v)||0]));
  const inv = Object.fromEntries(Object.entries(invitationTotals || {}).map(([k,v]) => [k, Number(v)||0]));

  return (
    <div className="dash-page">
      <h2 className="dash-welcome">
        👋 {t('admin.welcome')}, {user?.first_name || user?.username}!
      </h2>
      <p className="dash-role">
        {t('admin.loggedInAs')} <strong>{ROLE_LABELS[user?.role]}</strong>
      </p>

      {/* STAT CARDS */}
      <div className="stat-cards">
        <div className="stat-card clickable" onClick={() => navigate('/admin/users')}>
          <div className="stat-icon">👥</div>
          <div className="stat-value">{totals.total}</div>
          <div className="stat-label">{t('admin.totalUsers')}</div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">✅</div>
          <div className="stat-value stat-value--ok">{totals.active}</div>
          <div className="stat-label">{t('admin.active')}</div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">❌</div>
          <div className="stat-value stat-value--danger">{totals.unverified}</div>
          <div className="stat-label">{t('admin.unverified')}</div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">🛡️</div>
          <div className="stat-value stat-value--warn">{(totals.superadmins || 0) + (totals.admins || 0)}</div>
          <div className="stat-label">{t('admin.adminCount')}</div>
        </div>
        {totals.deleted > 0 && (
          <div className="stat-card">
            <div className="stat-icon">🗑️</div>
            <div className="stat-value" style={{ color: 'var(--text-muted)' }}>{totals.deleted}</div>
            <div className="stat-label">{t('admin.deleted')}</div>
          </div>
        )}
        <div className="stat-card" style={{ cursor: totals.locked > 0 ? 'pointer' : 'default' }}
             onClick={() => totals.locked > 0 && navigate('/admin/users')}>
          <div className="stat-icon">🔒</div>
          <div className="stat-value" style={{ color: totals.locked > 0 ? 'var(--error-border)' : 'var(--text-muted)' }}>
            {totals.locked ?? 0}
          </div>
          <div className="stat-label">{t('admin.lockedUsers')}</div>
        </div>
      </div>

      {/* SECOND ROW — cases / properties / partners / invitations */}
      <div className="stat-cards" style={{ marginTop: '0.75rem' }}>
        <div className="stat-card clickable" onClick={() => navigate('/dashboard')}>
          <div className="stat-icon">📋</div>
          <div className="stat-value">{ct.open ?? 0}</div>
          <div className="stat-label">Nyitott ügyek</div>
        </div>
        {ct.waiting_for_agent > 0 && (
          <div className="stat-card clickable" onClick={() => navigate('/dashboard')}>
            <div className="stat-icon">⏳</div>
            <div className="stat-value" style={{ color: 'var(--error-border)' }}>{ct.waiting_for_agent}</div>
            <div className="stat-label">Ügynökre vár</div>
          </div>
        )}
        <div className="stat-card">
          <div className="stat-icon">✨</div>
          <div className="stat-value" style={{ color: ct.new_today > 0 ? 'var(--success-text)' : 'var(--text-muted)' }}>{ct.new_today ?? 0}</div>
          <div className="stat-label">Új ügy ma</div>
        </div>
        <div className="stat-card clickable" onClick={() => navigate('/admin/properties')}>
          <div className="stat-icon">🏡</div>
          <div className="stat-value">{pt.active ?? 0}</div>
          <div className="stat-label">Aktív ingatlan</div>
        </div>
        <div className="stat-card clickable" onClick={() => navigate('/admin/partners')}>
          <div className="stat-icon">🤝</div>
          <div className="stat-value">{prt.active ?? 0}</div>
          <div className="stat-label">Aktív partner</div>
        </div>
        {inv.pending > 0 && (
          <div className="stat-card clickable" onClick={() => navigate('/admin/invitations')}>
            <div className="stat-icon">📨</div>
            <div className="stat-value">{inv.pending}</div>
            <div className="stat-label">Függő meghívó</div>
          </div>
        )}
      </div>

      {/* CHARTS */}
      <div className="dash-charts">
        <div className="chart-card">
          <h3 className="chart-title">📈 {t('admin.registrations14days')}</h3>
          <BarChart data={reg} color="#667eea" label="" />
          {reg.every(d => d.count === 0) && <p className="chart-empty">{t('admin.noData')}</p>}
        </div>
        <div className="chart-card">
          <h3 className="chart-title">🔐 {t('admin.logins')}</h3>
          <BarChart data={login} color="#48bb78" label="" />
          {login.every(d => d.count === 0) && <p className="chart-empty">{t('admin.noData')}</p>}
        </div>
        <div className="chart-card">
          <h3 className="chart-title">📋 Új ügyek (14 nap)</h3>
          <BarChart data={cases} color="#ed8936" label="" />
          {cases.every(d => d.count === 0) && <p className="chart-empty">{t('admin.noData')}</p>}
        </div>
        <div className="chart-card">
          <h3 className="chart-title">⚠️ {t('admin.failedLogins')}</h3>
          <BarChart data={failed} color="#fc8181" label="" />
          {failed.every(d => d.count === 0) && <p className="chart-empty">{t('admin.noData')}</p>}
        </div>
      </div>

      {/* RECENT ROWS */}
      <div className="dash-bottom">
        <div className="recent-card">
          <div className="recent-header">
            <h3>🕐 {t('admin.recentEvents')}</h3>
            <button className="recent-link" onClick={() => navigate('/admin/audit-logs')}>
              {t('admin.viewAll')}
            </button>
          </div>
          {(recentActivity || []).length === 0
            ? <p className="recent-empty">{t('admin.noEvents')}</p>
            : (recentActivity || []).map(e => (
              <div key={e.id} className="recent-item">
                <span className="recent-icon">{ACTION_ICONS[e.action] ?? '📝'}</span>
                <div className="recent-info">
                  <span className="recent-action">{e.action}</span>
                  <span className="recent-user">{e.user_email || 'anonymous'}</span>
                </div>
                <span className="recent-time">
                  {new Date(e.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </span>
              </div>
            ))
          }
        </div>

        <div className="recent-card">
          <div className="recent-header">
            <h3>🆕 {t('admin.recentRegistrations')}</h3>
            <button className="recent-link" onClick={() => navigate('/admin/users')}>
              {t('admin.viewAll')}
            </button>
          </div>
          {(recentUsers || []).length === 0
            ? <p className="recent-empty">{t('admin.noUsers')}</p>
            : (recentUsers || []).map(u => (
              <div key={u.id} className="recent-item" style={{ cursor: 'pointer' }}
                   onClick={() => setDrawerUserId(u.id)}>
                <div className="recent-avatar">
                  {(u.first_name?.[0] ?? u.email?.[0] ?? '?').toUpperCase()}
                </div>
                <div className="recent-info">
                  <span className="recent-action">
                    {[u.first_name, u.last_name].filter(Boolean).join(' ') || u.username || '—'}
                  </span>
                  <span className="recent-user">{u.email}</span>
                </div>
                <RoleBadge role={u.role} />
              </div>
            ))
          }
        </div>
      </div>

      {/* OPEN CASES */}
      {(recentCases || []).length > 0 && (
        <div className="dash-bottom" style={{ marginTop: '1.25rem' }}>
          <div className="recent-card" style={{ width: '100%' }}>
            <div className="recent-header">
              <h3>📋 Nyitott ügyek</h3>
              <button className="recent-link" onClick={() => navigate('/dashboard')}>
                {t('admin.viewAll')}
              </button>
            </div>
            {recentCases.map(c => (
              <div key={c.id} className="recent-item" style={{ cursor: 'pointer' }}
                   onClick={() => navigate(`/cases/${c.id}`)}>
                <span className="recent-icon">📋</span>
                <div className="recent-info">
                  <span className="recent-action">{c.title || `#${c.id}`}</span>
                  <span className="recent-user">{c.customer_email || c.customer_username || '—'}</span>
                </div>
                <span className={`recent-status status-${c.status}`}>{c.status}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {drawerUserId && (
        <ProfileDrawer userId={drawerUserId} onClose={() => setDrawerUserId(null)} />
      )}
    </div>
  );
};

export default AdminHomePage;
